import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/store";
import { useSelector } from "react-redux";
import { rentSelector } from "@redux/rent/selectors";
import { resetSelectedRackets } from "@redux/rent/slice";
import { setCartItemsToStorage, getCartItemsFromStorage, dateRestFormat } from "@utils/index";
import { openModal } from "react-url-modal";
import { ISelctedRacket } from "@redux/rent/types";
import { IAvailTime } from "../types";

interface UseAddToCartReturn {
  handleAddToCart: () => void;
  handleCloseModalUnavailable: () => void;
}

export const useAddToCart = (
  quantity: number,
  hasUnavailableQuantity: boolean,
  unavailableQuantityList: IAvailTime[],
  setIsOpenModalNoSelectedRackets: React.Dispatch<React.SetStateAction<boolean>>,
  setIsOpenModalUnavailable: React.Dispatch<React.SetStateAction<boolean>>,
  setUnAvailableQuantity: React.Dispatch<React.SetStateAction<IAvailTime[]>>
): UseAddToCartReturn => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { auth } = useAppSelector(state => state.authAndReg);
  const { date, selectedRackets } = useSelector(rentSelector);

  const handleCloseModalUnavailable = useCallback(() => {
    setIsOpenModalUnavailable(false);
    dispatch(resetSelectedRackets());
  }, [dispatch, date, setIsOpenModalUnavailable]);

  const handleAddToCart = useCallback(() => {
    if (Object.keys(selectedRackets).length === 0) {
      setIsOpenModalNoSelectedRackets(true);
      return;
    }

    if (!auth) {
      openModal({
        name: "login",
        params: {
          showWindow: "auth",
        },
      });
      return;
    }

    if (hasUnavailableQuantity) {
      setUnAvailableQuantity(unavailableQuantityList);
      setIsOpenModalUnavailable(true);
      return;
    }

    const dateAddedToCart = dateRestFormat(new Date());
    const cloneSelectedRackets = JSON.parse(
      JSON.stringify(selectedRackets)
    ) as { [key: string]: ISelctedRacket };

    Object.keys(cloneSelectedRackets).forEach((key) => {
      cloneSelectedRackets[key].startTimeRacket = dateAddedToCart;
      cloneSelectedRackets[key].count = quantity;
    });

    const currentStorageCartItems = getCartItemsFromStorage();
    const seatsStorage = currentStorageCartItems?.seats || {};
    const racketsStorage = currentStorageCartItems?.rackets || {};

    const cartItemsForStorage = {
      rackets: { ...racketsStorage, ...cloneSelectedRackets },
      seats: { ...seatsStorage },
    };

    const cartItemsForFetch = {
      cartTotal: {
        rackets: { ...cloneSelectedRackets },
        seats: {},
      },
    };

    setCartItemsToStorage(cartItemsForStorage);
    dispatch(resetSelectedRackets());
    navigate("/cart");
  }, [
    selectedRackets,
    auth,
    hasUnavailableQuantity,
    unavailableQuantityList,
    quantity,
    dispatch,
    navigate,
    setIsOpenModalNoSelectedRackets,
    setIsOpenModalUnavailable,
    setUnAvailableQuantity,
  ]);

  return {
    handleAddToCart,
    handleCloseModalUnavailable,
  };
};

